import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Eye, EyeOff } from 'lucide-react';
import { Alert } from '@/components/alert';
import { getApiErrorMessage } from '@/utils/ValidationErrors';
import { usePortal } from '@/portal/PortalProvider';
import { PortalAuthCard } from './PortalAuthCard';

const schema = Yup.object().shape({
  email: Yup.string().trim().email('Enter a valid email address.').required('Enter your email address.'),
  password: Yup.string().required('Enter your password.'),
});

/** Guest sign-in for the client portal (OL-193). */
const PortalLogin = () => {
  const { guest, loading, signIn } = usePortal();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  // Only paths inside the portal; anything else falls back to the matter list.
  const from = location.state?.from?.startsWith?.('/portal') ? location.state.from : '/portal';

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: schema,
    onSubmit: async (values, { setStatus }) => {
      setStatus(null);
      try {
        await signIn(values.email.trim(), values.password);
        navigate(from, { replace: true });
      } catch (error) {
        setStatus(getApiErrorMessage(error, 'Sign-in failed. Please try again.'));
      }
    },
  });

  if (!loading && guest) {
    return <Navigate to="/portal" replace />;
  }

  return (
    <PortalAuthCard title="Sign in" subtitle="View the documents your firm has shared with you.">
      <form className="flex flex-col gap-5" onSubmit={formik.handleSubmit} noValidate>
        {formik.status && <Alert variant="danger">{formik.status}</Alert>}

        <div className="flex flex-col gap-1">
          <label className="form-label text-gray-900" htmlFor="portal-email">Email</label>
          <label className="input">
            <input
              id="portal-email"
              type="email"
              autoComplete="username"
              placeholder="you@example.com"
              {...formik.getFieldProps('email')}
              className={clsx('form-control', { 'is-invalid': formik.touched.email && formik.errors.email })}
            />
          </label>
          {formik.touched.email && formik.errors.email && (
            <span role="alert" className="text-danger text-xs mt-1">{formik.errors.email}</span>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-1">
            <label className="form-label text-gray-900" htmlFor="portal-password">Password</label>
            <Link to="/portal/forgot-password" className="text-2sm link shrink-0">
              Forgot password?
            </Link>
          </div>
          <label className="input">
            <input
              id="portal-password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Your password"
              {...formik.getFieldProps('password')}
              className={clsx('form-control', { 'is-invalid': formik.touched.password && formik.errors.password })}
            />
            <button
              type="button"
              className="btn btn-icon"
              onClick={() => setShowPassword((shown) => !shown)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} className="text-gray-500" /> : <Eye size={16} className="text-gray-500" />}
            </button>
          </label>
          {formik.touched.password && formik.errors.password && (
            <span role="alert" className="text-danger text-xs mt-1">{formik.errors.password}</span>
          )}
        </div>

        <button type="submit" className="btn btn-dark flex justify-center grow" disabled={formik.isSubmitting}>
          {formik.isSubmitting ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="text-2xs text-gray-500 text-center">
          First time here? Use the link in your invitation email to choose a password.
        </p>
      </form>
    </PortalAuthCard>
  );
};

export { PortalLogin };
